import { z } from 'zod'
import type { CalculatorDefinition } from '@/types/calculator'
import { positiveMoney } from '@/calculators/shared/schema'
import { ANALYSIS_YEARS, calculateSolarRoi } from './calculation'
import { content, faqs } from './content'
import type { SolarRoiInputs, SolarRoiResult } from './types'

const schema = z.object({
  monthlyUsageKwh: z.number().min(1, { message: 'Enter monthly usage in kWh' }),
  tariffPerKwh: positiveMoney('Enter a tariff').refine((v) => v > 0, {
    message: 'Tariff must be greater than 0',
  }),
  daytimeUsageShare: z.number().min(0).max(1),
  systemCostPerW: positiveMoney('Enter a system cost').refine((v) => v > 0, {
    message: 'Cost per watt must be greater than 0',
  }),
  systemSizeKw: z.number().min(0.1, { message: 'System size must be at least 0.1 kW' }),
  annualTariffEscalation: z.number().min(0).max(1),
  financingYears: z.number().int().min(0).max(20),
  financingRate: z.number().min(0).max(1),
})

const defaults: SolarRoiInputs = {
  monthlyUsageKwh: 900,
  tariffPerKwh: 3.5,
  daytimeUsageShare: 0.75,
  systemCostPerW: 15,
  systemSizeKw: 5,
  annualTariffEscalation: 0.08,
  financingYears: 0,
  financingRate: 0.12,
}

export const solarRoiDefinition: CalculatorDefinition<SolarRoiInputs, SolarRoiResult> = {
  id: 'solar-roi',
  slug: 'solar-calculator',
  category: 'energy',
  title: 'Solar System ROI Calculator',
  shortTitle: 'Solar ROI',
  description:
    'Estimate solar system savings, payback and ROI in South Africa. Includes editable assumptions and a wide uncertainty warning.',
  inputs: [
    {
      name: 'monthlyUsageKwh',
      label: 'Monthly electricity usage',
      type: 'number',
      required: true,
      unit: 'kWh',
      min: 1,
      step: 1,
      defaultValue: 900,
    },
    {
      name: 'tariffPerKwh',
      label: 'Electricity tariff',
      type: 'money',
      required: true,
      unit: 'R/kWh',
      min: 0,
      step: 0.01,
      defaultValue: 3.5,
    },
    {
      name: 'daytimeUsageShare',
      label: 'Daytime electricity use',
      type: 'percentage',
      required: true,
      unit: '%',
      min: 0,
      max: 1,
      step: 0.01,
      defaultValue: 0.75,
      hint: 'Share of daytime usage that solar can directly offset',
    },
    {
      name: 'systemSizeKw',
      label: 'System size',
      type: 'number',
      required: true,
      unit: 'kW',
      min: 0.1,
      step: 0.1,
      defaultValue: 5,
    },
    {
      name: 'systemCostPerW',
      label: 'System cost per watt',
      type: 'money',
      required: true,
      unit: 'R/W',
      min: 0,
      step: 0.01,
      defaultValue: 15,
    },
    {
      name: 'annualTariffEscalation',
      label: 'Annual tariff escalation',
      type: 'percentage',
      required: true,
      unit: '%',
      min: 0,
      max: 1,
      step: 0.01,
      defaultValue: 0.08,
    },
    {
      name: 'financingYears',
      label: 'Finance term (0 = cash)',
      type: 'number',
      required: true,
      unit: 'years',
      min: 0,
      max: 20,
      step: 1,
      defaultValue: 0,
    },
    {
      name: 'financingRate',
      label: 'Finance interest rate',
      type: 'percentage',
      required: true,
      unit: '%',
      min: 0,
      max: 1,
      step: 0.001,
      defaultValue: 0.12,
    },
  ],
  defaults,
  schema,
  calculate: calculateSolarRoi,
  resultSections: [
    {
      id: 'paybackYears',
      label: 'Simple payback (years)',
      format: 'number',
      value: (r: SolarRoiResult) => r.paybackYears,
      highlighted: true,
    },
    {
      id: 'simpleRoi',
      label: '25-year simple ROI',
      format: 'percent',
      value: (r: SolarRoiResult) => r.simpleRoi,
      highlighted: true,
    },
    {
      id: 'totalSystemCost',
      label: 'Total system cost',
      format: 'currency',
      value: (r: SolarRoiResult) => r.totalSystemCost,
    },
    {
      id: 'estimatedAnnualGenerationKwh',
      label: 'Estimated annual generation',
      format: 'number',
      value: (r: SolarRoiResult) => r.estimatedAnnualGenerationKwh,
    },
    {
      id: 'estimatedAnnualSavingsYear1',
      label: 'Year 1 estimated savings',
      format: 'currency',
      value: (r: SolarRoiResult) => r.estimatedAnnualSavingsYear1,
    },
    {
      id: 'financedMonthlyPayment',
      label: 'Estimated finance payment',
      format: 'currency',
      value: (r: SolarRoiResult) => r.financedMonthlyPayment,
    },
    {
      id: 'netSavingsOver25Years',
      label: 'Net 25-year savings',
      format: 'currency',
      value: (r: SolarRoiResult) => r.netSavingsOver25Years,
    },
  ],
  assumptions: [
    {
      name: 'CSIR / SAPVIA solar resource and cost assumptions',
      url: 'https://www.csir.co.za/',
      reviewedAt: '2026-09-11',
      effectiveFrom: '2026-09-01',
      notes:
        'Assumes roughly 1,650 kWh of generation per installed kWp per year with a single orientation. Tariff escalation, financing rates and installation costs are user-editable assumptions, not forecasts.',
    },
  ],
  related: ['bond-calculator', 'rental-yield-calculator'],
  seo: {
    title: 'Solar Payback Calculator South Africa',
    description: `Estimate solar payback, savings and ${ANALYSIS_YEARS}-year ROI in South Africa with this solar payback calculator — editable tariffs and costs.`,
    keywords: ['solar calculator', 'solar ROI South Africa', 'solar payback', 'solar savings'],
  },
  content,
  faqs,
}
